// Coerce whatever was thrown/rejected into a real Error with a message that says
// something.
//
// Most of our failure paths reject with a PLAIN OBJECT, not an Error: useFetch
// rejects with `FetchError` ({ message, status, endpoint, body, timedOut }), and
// third-party SDKs reject with Response objects or bare payloads. The old
// `new Error(String(value))` turned every one of those into "[object Object]" —
// which became both the Bugsnag message AND (via computeGrouping's
// normalizeMessage) the dashboard title, so every distinct failure inside one
// category piled into a single useless row ("kbm · [PipeOrders] Queue:
// [object Object]").
//
// Keep the extraction shallow and allocation-free on the happy path: real Errors
// pass straight through untouched (their stack is what Bugsnag groups on when we
// have no grouping info).

const MAX_MESSAGE_LENGTH = 300
const MAX_BODY_LENGTH = 500
const MAX_DEPTH = 2

const asText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

const truncate = (text: string, max: number): string =>
  text.length > max ? `${text.slice(0, max)}…` : text

// JSON as a last resort. Circular structures / getters that throw must not take
// down the error reporter itself.
const safeJson = (value: unknown, max: number): string | undefined => {
  try {
    const json = JSON.stringify(value)
    if (!json || json === '{}' || json === '[]' || json === 'null') return undefined
    return truncate(json, max)
  } catch {
    return undefined
  }
}

// Best human-readable description of an arbitrary thrown value, or undefined when
// there is genuinely nothing to say (null, empty object, unserializable).
export function describeErrorValue(value: unknown, depth = 0): string | undefined {
  if (value == null) return undefined
  if (value instanceof Error) return asText(value.message) ?? asText(value.name)

  const direct = asText(value)
  if (direct) return truncate(direct, MAX_MESSAGE_LENGTH)
  // number / boolean / bigint / symbol — String() is meaningful for these.
  if (typeof value !== 'object') return truncate(String(value), MAX_MESSAGE_LENGTH)

  const record = value as Record<string, unknown>

  // `message` covers useFetch's FetchError, DOMException-likes, and NestJS error
  // bodies. A validation body's `message` can be an array of strings.
  const own =
    asText(record.message) ??
    (Array.isArray(record.message) ? asText(record.message.filter(Boolean).join('; ')) : undefined)
  if (own) return truncate(own, MAX_MESSAGE_LENGTH)

  // SDKs commonly wrap the real failure one level down.
  if (depth < MAX_DEPTH) {
    for (const key of ['error', 'reason', 'cause', 'data', 'body'] as const) {
      const nested = describeErrorValue(record[key], depth + 1)
      if (nested) return nested
    }
  }

  // Response / XHR-shaped rejections carry no message, only a status.
  const status = typeof record.status === 'number' ? record.status : undefined
  const statusText = asText(record.statusText)
  if (status != null || statusText) {
    const url = asText(record.url) ?? asText(record.endpoint)
    return truncate(
      ['HTTP', status ?? '?', statusText, url].filter((part) => part != null).join(' '),
      MAX_MESSAGE_LENGTH,
    )
  }

  return safeJson(record, MAX_MESSAGE_LENGTH)
}

// Always returns an Error, so Bugsnag never has to guess. Real Errors are
// returned as-is (identity matters: `attachPipeErrInfo` writes the grouping info
// onto this object and the config `onError` reads it back off
// `event.originalError`).
export function toError(value: unknown): Error {
  if (value instanceof Error) return value
  const error = new Error(describeErrorValue(value) ?? 'Unknown error (non-Error value thrown)')
  // Adopt a stack captured at the throw site (useFetch stamps one on its
  // FetchError) — otherwise every coerced error is stacked at this coercion
  // point, which tells us nothing about where it failed.
  if (value && typeof value === 'object') {
    const stack = (value as { stack?: unknown }).stack
    if (typeof stack === 'string' && stack !== '') {
      try {
        error.stack = stack
      } catch {
        // read-only stack on some engines — keep the coercion-point stack
      }
    }
  }
  return error
}

// Diagnostics lifted off a non-Error rejection so they land on the Bugsnag
// `metadata` tab instead of being lost with the stringified object. Key names
// match the ones computeGrouping writes (`url` / `method`), so a dashboard pivot
// on url/method covers these errors too.
export function extractErrorFields(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || value instanceof Error) return undefined
  const record = value as Record<string, unknown>
  const fields: Record<string, unknown> = {}

  const url = asText(record.endpoint) ?? asText(record.url)
  if (url) fields.url = url
  const method = asText(record.method)
  if (method) fields.method = method
  // `statusCode` is the Nest error-body spelling of the same thing.
  const status =
    typeof record.status === 'number'
      ? record.status
      : typeof record.statusCode === 'number'
        ? record.statusCode
        : undefined
  if (status != null) fields.status = status
  const statusText = asText(record.statusText)
  if (statusText) fields.statusText = statusText
  if (typeof record.timedOut === 'boolean') fields.timedOut = record.timedOut
  const code = asText(record.code) ?? (typeof record.code === 'number' ? record.code : undefined)
  if (code != null) fields.code = code
  const name = asText(record.name)
  if (name) fields.name = name

  // The server's rejection reason (e.g. which field failed a 422) — stringified
  // and truncated so a large payload can't bloat the event.
  if (record.body !== undefined) {
    const body = asText(record.body) ?? safeJson(record.body, MAX_BODY_LENGTH)
    if (body) fields.responseBody = truncate(body, MAX_BODY_LENGTH)
  }

  return Object.keys(fields).length > 0 ? fields : undefined
}
